import { useRef, useState, useEffect } from "react";
import { Dialog, DialogPopup, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Checkbox } from "@/components/ui/Checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { ChevronRight, CheckCircle, Close } from "@mui/icons-material";
import { toast } from "sonner";
import { cn } from "@/utils/Functions";
import { motion, AnimatePresence } from "motion/react";
import { useDemoContractAnalysis } from "@/hooks/contracts/useDemoContractAnalysis";
import { drawLetter } from "@/utils/signature/utils";
import { signatureStyles } from "@/utils/signature/styles";

type User = {
  id: string;
  name: string;
  email: string;
  profilePicture: string;
  signed?: boolean;
  signedAt?: string;
}

type ContractSignProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signers?: User[];
}

function ContractSign({ open, onOpenChange, signers = [] }: ContractSignProps) {
  const { demoContractSignData } = useDemoContractAnalysis();
  const signatureMainRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState(demoContractSignData.fullName);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSignatureActive, setIsSignatureActive] = useState(!!demoContractSignData.fullName);
  const previousNameRef = useRef(demoContractSignData.fullName || '');
  const [prefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  const [currentDate] = useState(new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }));

  const handleNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key;
    const input = event.currentTarget;
    
    if (key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      if (fullName.trim() && agreedToTerms) {
        handleSave();
      }
      return;
    }
    
    // Handle regular keys
    if (key.length === 1 && /[a-zA-Z ]/.test(key)) {
      // The onChange handler will handle drawing the letter
      return;
    } else if (event.code === 'Backspace') {
      setTimeout(() => {
        if (signatureMainRef.current) {
          signatureMainRef.current.innerHTML = '';
          const letters = input.value.split('');
          letters.forEach((letter) => {
            drawLetter(letter, false, signatureMainRef, prefersReducedMotion);
          });
        }
      }, 50);
    }

    setTimeout(() => {
      if (!input.value) {
        setIsSignatureActive(false);
      } else {
        setIsSignatureActive(true);
      }
    }, 50);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const previousValue = previousNameRef.current;
    previousNameRef.current = value;
    setFullName(value);
    
    if (!value.trim()) {
      setIsSignatureActive(false);
      if (signatureMainRef.current) {
        signatureMainRef.current.innerHTML = '';
      }
    } else {
      setIsSignatureActive(true);
      if (signatureMainRef.current) {
        signatureMainRef.current.innerHTML = '';
        const letters = value.split('');
        letters.forEach((letter, index) => {
          const isNewLetter = index >= previousValue.length && !prefersReducedMotion;
          drawLetter(letter, isNewLetter, signatureMainRef, prefersReducedMotion);
        });
      }
    }
  };

  useEffect(() => {
    // Update signature display when fullName changes externally
    if (signatureMainRef.current && fullName) {
      signatureMainRef.current.innerHTML = '';
      const letters = fullName.split('');
      letters.forEach((letter) => {
        drawLetter(letter, false, signatureMainRef, prefersReducedMotion);
      });
      setIsSignatureActive(true);
    } else if (!fullName) {
      setIsSignatureActive(false);
    }
  }, [fullName]);

  // Initialize signature when dialog opens with prefilled name
  useEffect(() => {
    if (open && fullName) {
      // Small delay to ensure ref is attached to DOM
      setTimeout(() => {
        if (signatureMainRef.current) {
          signatureMainRef.current.innerHTML = '';
          const letters = fullName.split('');
          letters.forEach((letter) => {
            drawLetter(letter, false, signatureMainRef, prefersReducedMotion);
          });
          setIsSignatureActive(true);
        }
      }, 50);
    }
  }, [open, fullName]);

  const handleClear = () => {
    setFullName('');
    setIsSignatureActive(false);
    previousNameRef.current = '';
    if (signatureMainRef.current) {
      signatureMainRef.current.innerHTML = '';
    }
  };

  const validateSignature = (): boolean => {
    if (!fullName.trim()) {
      toast.error('Please enter your full legal name');
      return false;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the terms to sign');
      return false;
    }

    return true;
  };

  const handleSave = () => {
    console.log('handleSave called', { fullName, agreedToTerms });
    
    if (!validateSignature()) {
      console.log('Validation failed');
      return;
    }

    console.log('Validation passed');

    const signatureInfo = {
      fullName,
      date: new Date().toISOString(),
      timestamp: Date.now(),
      agreedToTerms,
    };

    console.log('Signature saved:', signatureInfo);
    
    toast.success('Contract signed successfully!');
    
    // Show success animation
    setShowSuccess(true);

    // Close modal after animation
    setTimeout(() => {
      onOpenChange(false);
      setShowSuccess(false);
      // Reset form
      setFullName('');
      setAgreedToTerms(false);
      setIsSignatureActive(false);
      previousNameRef.current = '';
      if (signatureMainRef.current) {
        signatureMainRef.current.innerHTML = '';
      }
    }, 2500);
  };

  return (
    <>
      <style>{signatureStyles}</style>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPopup className="p-0 rounded-3xl w-[calc(100%-3rem)] md:w-[640px] h-[70vh] flex flex-col overflow-hidden">
        <AnimatePresence mode="wait">
          {showSuccess ? (
            <motion.div
              key="success"
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.8 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3 }}
              className="flex flex-col items-center justify-center py-16 px-6"
            >
              <motion.div
                initial={prefersReducedMotion ? { scale: 1 } : { scale: 0 }}
                animate={{ scale: 1 }}
                transition={prefersReducedMotion ? { duration: 0 } : { 
                  delay: 0.1,
                  type: "spring",
                  stiffness: 200,
                  damping: 15
                }}
              >
                <CheckCircle 
                  className="text-green-400 mb-6" 
                  sx={{ fontSize: 80 }}
                />
              </motion.div>
              <motion.h2
                initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.3 }}
                className="text-2xl font-semibold text-white mb-2"
              >
                Contract Signed Successfully!
              </motion.h2>
              <motion.p
                initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.4 }}
                className="text-neutral-400 text-center"
              >
                Signed by {fullName} on {currentDate}
              </motion.p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 1 }}
              exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }}
              className="flex flex-col h-full"
            >
              <div className="px-6 pt-6">
                <DialogHeader>
                  <DialogTitle>Sign Contract</DialogTitle>
                </DialogHeader>
                <DialogDescription className="hidden sr-only">
                  Sign the contract with your signature.
                </DialogDescription>
                <hr className="my-4 border-white/10" />
              </div>
              
              <div className="flex-1 overflow-y-auto custom-scrollbar px-6">
                <div className="space-y-6 pb-6">
          {/* Signers Avatar Stack */}
          {signers.length > 0 && (
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-neutral-400 mb-3">Signers</p>
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {signers.map((signer) => (
                    <Tooltip key={signer.id}>
                      <TooltipTrigger render={<img
                          src={signer.profilePicture}
                          alt={signer.name}
                          className="w-10 h-10 rounded-full border-2 border-neutral-900 cursor-pointer hover:scale-110 transition-transform object-cover"
                        />} />
                      <TooltipContent side="top" className="text-xs">
                        <div className="text-center">
                          <p className="font-medium">{signer.name}</p>
                          {signer.signed && (
                            <p className="text-green-300 text-xs">✓ Signed {signer.signedAt}</p>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
                <span className="text-sm text-neutral-400 ml-2">
                  {signers.filter(s => s.signed).length} of {signers.length} signed
                </span>
              </div>
            </div>
          )}

          {/* Name Input with Animated Signature */}
          <div className="space-y-2">
            <label className="text-sm text-neutral-400" htmlFor="fullName">
              Full Legal Name <span className="text-red-400">*</span>
            </label>
            <div className={cn("relative bg-white/5 rounded-2xl transition-all duration-400", {
              "p-6": isSignatureActive,
              "p-4": !isSignatureActive,
            })}>
              <div className="relative">
                <Input
                  ref={nameInputRef}
                  id="fullName"
                  name="fullName"
                  placeholder="Your name"
                  value={fullName}
                  onChange={handleNameChange}
                  onKeyDown={handleNameKeyDown}
                  onPaste={(e) => {
                    // onChange handler will process the pasted content
                  }}
                  autoComplete="name"
                  maxLength={256}
                  className={cn("h-12 bg-transparent border-white/20 text-white placeholder:text-white/50 focus:border-white/50 pr-10 touch-manipulation", {
                    "border-white/50": isSignatureActive,
                  })}
                  style={{
                    touchAction: 'manipulation',
                  }}
                />
                {fullName.trim() && (
                  <button
                    type="button"
                    onClick={handleClear}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClear();
                      }
                    }}
                    aria-label="Clear input"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900 transition-colors touch-manipulation"
                    style={{
                      minWidth: '44px',
                      minHeight: '44px',
                      touchAction: 'manipulation',
                    }}
                  >
                    <Close className="size-4" />
                  </button>
                )}
              </div>

              {/* Signature Display */}
              {isSignatureActive && (
                <div className="mt-6 pt-4 border-t border-white/20">
                  <div className="text-sm text-white/50 mb-2 font-mono uppercase">Signed by,</div>
                  <div 
                    ref={signatureMainRef}
                    className="signature-main flex flex-wrap justify-start min-h-[51px] [&_svg_path]:stroke-white [&_svg_path]:stroke-1 [&_svg_path]:stroke-linejoin-round [&_svg_path]:stroke-linecap-round"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Date and Terms Row */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-4">
            {/* Legal Disclaimer */}
            <div className="bg-white/5 p-4 rounded-xl">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                  id="terms"
                />
                <label 
                  htmlFor="terms" 
                  className="text-xs text-neutral-300 leading-relaxed cursor-pointer"
                  dangerouslySetInnerHTML={{ __html: demoContractSignData.signatureTerms }}
                />
              </div>
            </div>

            {/* Date Display */}
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-neutral-400">Signing Date</p>
              <p className="text-base text-white font-medium">{currentDate}</p>
            </div>
          </div>
                </div>
              </div>
            
              <div className="sticky bottom-0 bg-neutral-900 border-t border-white/10 px-6 py-4">
                <DialogFooter>
                  <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={
                  !agreedToTerms || !fullName.trim()
                }
              >
                Sign Contract
                <ChevronRight />
              </Button>
                </DialogFooter>
              </div>
            </motion.div>
        )}
        </AnimatePresence>
      </DialogPopup>
    </Dialog>
    </>
  )
}

export {
  ContractSign,
}
